import { get, post } from "@/lib/api";

function isMissingEndpointError(error) {
  const status = Number(error?.status || 0);
  return status === 404 || status === 405 || status === 501;
}

/**
 * Ask backend whether current user still has an active payment session.
 */
export async function getActivePaymentSession() {
  return get("/payment-sessions/active", { useAuth: true, useApiKey: true });
}

/**
 * Fetch active payment channel codes for checkout/payment session creation.
 */
export async function getPaymentChannels() {
  return get("/payment-channels", { useAuth: true, useApiKey: true });
}

/**
 * Ask backend to create an order + Xendit Payment Session.
 * Backend should call Xendit Create Session API with secret key.
 */
export async function createPaymentSession(payload, options = {}) {
  const explicitIdempotencyKey = String(options?.idempotencyKey || "").trim();
  const payloadIdempotencyKey = String(payload?.idempotency_key || "").trim();
  const requestPayload = (
    explicitIdempotencyKey && !payloadIdempotencyKey
      ? { ...payload, idempotency_key: explicitIdempotencyKey }
      : payload
  );

  return post("/payment-sessions", requestPayload, {
    useAuth: true,
    useApiKey: true,
  });
}

/**
 * Ask backend to fetch latest Xendit Payment Session status.
 */
export async function getPaymentSession(sessionId) {
  return get(`/payment-sessions/${sessionId}`, { useAuth: true, useApiKey: true });
}

/**
 * Fetch payment session bootstrap payload for Xendit Components initialization.
 * Falls back to legacy GET /payment-sessions/:id endpoint when bootstrap route
 * is not available yet.
 */
export async function getPaymentSessionBootstrap(sessionId) {
  try {
    return await get(`/payment-sessions/${sessionId}/bootstrap`, {
      useAuth: true,
      useApiKey: true,
    });
  } catch (error) {
    if (isMissingEndpointError(error)) {
      return getPaymentSession(sessionId);
    }
    throw error;
  }
}

/**
 * Fetch lightweight payment session status for polling.
 * Falls back to legacy payload when /status endpoint is unavailable.
 */
export async function getPaymentSessionStatus(sessionId) {
  try {
    return await get(`/payment-sessions/${sessionId}/status`, {
      useAuth: true,
      useApiKey: true,
    });
  } catch (error) {
    if (isMissingEndpointError(error)) {
      return getPaymentSession(sessionId);
    }
    throw error;
  }
}

/**
 * Fetch combined realtime payload (session_status + order_status).
 * Returns null when endpoint is not available so caller can fallback.
 */
export async function getPaymentSessionRealtime(sessionId) {
  try {
    return await get(`/payment-sessions/${sessionId}/realtime`, {
      useAuth: true,
      useApiKey: true,
    });
  } catch (error) {
    if (isMissingEndpointError(error)) {
      return null;
    }
    throw error;
  }
}

/**
 * Ask backend to cancel an active Xendit Payment Session.
 */
export async function cancelPaymentSession(sessionId) {
  return post(`/payment-sessions/${sessionId}/cancel`, {}, { useAuth: true, useApiKey: true });
}
